import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Settings, Save, RefreshCw, AlertTriangle, Users, Target, Zap, DollarSign
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSettings = () => {
  const { token, admin } = useOutletContext();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    fetchSettings();
  }, [token]);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/settings?token=${token}`);
      setSettings(response.data);
      setForm(response.data);
    } catch (error) {
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams({ token });
      Object.entries(form).forEach(([key, value]) => {
        if (key !== 'id' && key !== 'updated_at' && key !== 'updated_by') {
          params.append(key, value);
        }
      });
      
      await axios.put(`${API}/admin/settings?${params}`);
      toast.success('Settings saved successfully');
      fetchSettings();
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const canEdit = admin?.role === 'super_admin' || admin?.permissions?.includes('settings');

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">System Settings</h2>
          <p className="text-slate-400 text-sm">Configure platform-wide settings</p>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving || !canEdit}
          className="bg-gradient-to-r from-red-500 to-orange-500"
        >
          {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Save Changes
        </Button>
      </div>

      {!canEdit && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            <p className="text-amber-500 text-sm">You don't have permission to edit settings</p>
          </div>
        </div>
      )}

      {/* Limits Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-500/20 flex items-center justify-center">
            <Target className="w-5 h-5 text-blue-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Platform Limits</h3>
            <p className="text-sm text-slate-400">Set maximum allowed values</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="text-slate-400">Max Groups per User</Label>
            <Input
              type="number"
              value={form.max_groups_per_user || 10}
              onChange={(e) => setForm({ ...form, max_groups_per_user: parseInt(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-slate-400">Max Members per Group</Label>
            <Input
              type="number"
              value={form.max_members_per_group || 50}
              onChange={(e) => setForm({ ...form, max_members_per_group: parseInt(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-slate-400">Max Expense Amount (₹)</Label>
            <Input
              type="number"
              value={form.max_expense_amount || 1000000}
              onChange={(e) => setForm({ ...form, max_expense_amount: parseFloat(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
        </div>
      </motion.div>

      {/* Gamification Settings */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-purple-500/20 flex items-center justify-center">
            <Zap className="w-5 h-5 text-purple-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Gamification</h3>
            <p className="text-sm text-slate-400">Configure XP rewards</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="text-slate-400">XP per Contribution</Label>
            <Input
              type="number"
              value={form.xp_per_contribution || 20}
              onChange={(e) => setForm({ ...form, xp_per_contribution: parseInt(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-slate-400">XP per Expense Added</Label>
            <Input
              type="number"
              value={form.xp_per_expense || 10}
              onChange={(e) => setForm({ ...form, xp_per_expense: parseInt(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
          <div>
            <Label className="text-slate-400">XP per Signup</Label>
            <Input
              type="number"
              value={form.xp_per_signup || 50}
              onChange={(e) => setForm({ ...form, xp_per_signup: parseInt(e.target.value) })}
              disabled={!canEdit}
              className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
            />
          </div>
        </div>
      </motion.div>

      {/* Access Control */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-green-500/20 flex items-center justify-center">
            <Users className="w-5 h-5 text-green-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Access Control</h3>
            <p className="text-sm text-slate-400">Manage user registration</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
            <div>
              <p className="font-medium text-white">Allow New Registrations</p>
              <p className="text-sm text-slate-400">Enable or disable new user signups</p>
            </div>
            <Switch
              checked={form.allow_new_registrations !== false}
              onCheckedChange={(checked) => setForm({ ...form, allow_new_registrations: checked })}
              disabled={!canEdit}
            />
          </div>
        </div>
      </motion.div>

      {/* Maintenance Mode */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-900 rounded-2xl border border-slate-800 p-6"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
            <AlertTriangle className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="font-semibold text-white">Maintenance Mode</h3>
            <p className="text-sm text-slate-400">Take the platform offline for maintenance</p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
            <div>
              <p className="font-medium text-white">Enable Maintenance Mode</p>
              <p className="text-sm text-slate-400">Users will see a maintenance message</p>
            </div>
            <Switch
              checked={form.maintenance_mode === true}
              onCheckedChange={(checked) => setForm({ ...form, maintenance_mode: checked })}
              disabled={!canEdit}
            />
          </div>

          {form.maintenance_mode && (
            <div>
              <Label className="text-slate-400">Maintenance Message</Label>
              <Input
                value={form.maintenance_message || ''}
                onChange={(e) => setForm({ ...form, maintenance_message: e.target.value })}
                disabled={!canEdit}
                placeholder="We're currently performing maintenance..."
                className="mt-2 bg-slate-800 border-slate-700 text-white rounded-xl"
              />
            </div>
          )}
        </div>
      </motion.div>

      {/* Last Updated */}
      {settings?.updated_at && (
        <p className="text-center text-sm text-slate-500">
          Last updated: {new Date(settings.updated_at).toLocaleString()}
        </p>
      )}
    </div>
  );
};

export default AdminSettings;
